import { PMTiles, type Protocol, type RangeResponse, type Source } from "pmtiles"
import type { IMapTilesSource } from "@ports/app"

/**
 * Native tiles source. Capacitor's bundled-asset server doesn't honour HTTP
 * Range requests, so PMTiles (which range-reads its directory + tiles) can't
 * stream the basemap on device — only the whole-GET GeoJSON layers render.
 * The regional archives are tiny (~1 MB), so fetch each whole (a plain GET,
 * which DOES work) into memory once and register it with the pmtiles Protocol
 * as an in-RAM Source that slices ranges out of the ArrayBuffer. No file copy,
 * no Range request ever leaves the WebView.
 *
 * `resolve` returns the archive KEY; the style uses it as `pmtiles://<key>`,
 * which the protocol resolves to this pre-registered Source. Non-pmtiles
 * assets are plain GETs and pass straight through to the WebView origin.
 */
class BufferSource implements Source {
  constructor(
    private readonly key: string,
    private readonly buf: ArrayBuffer,
  ) {}

  getKey(): string {
    return this.key
  }

  async getBytes(offset: number, length: number): Promise<RangeResponse> {
    return { data: this.buf.slice(offset, offset + length) }
  }
}

export function useInMemoryTilesSource(protocol: Protocol): IMapTilesSource {
  const archives = new Map<string, Promise<string>>()

  async function register(rel: string): Promise<string> {
    const res = await fetch(`/${rel}`)
    if (!res.ok) throw new Error(`tiles: cannot read bundled ${rel} (${res.status})`)
    protocol.add(new PMTiles(new BufferSource(rel, await res.arrayBuffer())))
    return rel
  }

  return {
    async resolve(tilesAsset) {
      const rel = tilesAsset.replace(/^\//, "")
      if (!rel.endsWith(".pmtiles")) return `/${rel}`
      let archive = archives.get(rel)
      if (!archive) {
        archive = register(rel)
        archives.set(rel, archive)
      }
      return archive
    },
  }
}
